// Top-bar support menu (MoeGo-style). A headset icon in the header with a
// red dot for unread support replies; clicking it opens a dropdown with the
// support entry points. Selecting one calls onSelect(entry) — the shell then
// opens the Support view in that mode:
//   'live'       → support landing (AI assistant + tickets)
//   'suggestion' → new ticket pre-set to the "suggestion" category
//   'help'       → AI assistant (grounded on the Deskte manual)

import { useEffect, useRef, useState } from 'react';
import { Headphones, LifeBuoy, Lightbulb, BookOpen, Ticket } from 'lucide-react';

export function SupportMenu({ lang, unread = 0, onSelect }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = [
    // Direct entry to the Support landing (list of YOUR tickets + "new ticket").
    // Was hard to reach before — you had to open a ticket and go back for the
    // list to appear (Lissette 2026-07-14).
    { key: 'tickets', Icon: Ticket, color: '#00d4ff', label: es ? 'Soporte y mis tickets' : 'Support & my tickets', sub: es ? 'Ver tus tickets o crear uno nuevo' : 'See your tickets or open a new one' },
    { key: 'live', Icon: LifeBuoy, color: '#34d399', label: es ? 'Chat de soporte (IA)' : 'Support chat (AI)', sub: es ? 'Chatea con el asistente IA ahora' : 'Chat with the AI assistant now' },
    { key: 'suggestion', Icon: Lightbulb, color: '#f4c430', label: es ? 'Sugerir una función' : 'Feature request', badge: es ? 'Nuevo' : 'New', sub: es ? 'Comparte una idea para Deskte' : 'Share an idea for Deskte' },
    { key: 'help', Icon: BookOpen, color: '#a78bfa', label: es ? 'Centro de ayuda' : 'Help center', sub: es ? 'Artículos y guías de Deskte' : 'Deskte articles & guides' },
  ];

  const pick = (key) => { setOpen(false); onSelect?.(key); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="dkt-hpill"
        title={es ? 'Soporte' : 'Support'}
        style={{ position: 'relative' }}
      >
        <Headphones size={14} style={{ color: unread > 0 ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }} />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 100, background: 'var(--c-red,#ef4444)', color: '#fff', fontSize: '.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50, width: 280, maxWidth: 'calc(100vw - 32px)', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,.5)', overflow: 'hidden', padding: '.35rem' }}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => pick(it.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '.6rem .65rem', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--c-text,#c8dff0)', fontFamily: 'inherit', borderRadius: 8 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface2,#112538)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: it.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <it.Icon size={15} style={{ color: it.color }} />
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{it.label}</span>
                  {it.badge && <span style={{ fontSize: '.58rem', fontWeight: 800, color: '#a78bfa', border: '1px solid #a78bfa', borderRadius: 5, padding: '0 4px', lineHeight: 1.4 }}>{it.badge}</span>}
                </span>
                <span style={{ display: 'block', fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 1 }}>{it.sub}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
